import { useMemo } from "react";
import { NumberLike } from "@visx/scale";

import Box from "ds/components/Box";
import Badge from "ds/components/Badge";

import DoraAverage from "../components/Average";
import DoraEmpty from "../components/Empty";
import DoraRange from "../components/DoraRange";
import { getDoraGrade } from "../getDoraGrade";
import useDoraChartType from "../useChartType";
import useDoraChangeFailureRate from "./useRate";
import useDoraChangeFailureRateRange from "./useRange";
import { CHART_TYPE_STORAGE_KEY, CHANGE_FAILURE_RATE_THRESHOLDS } from "./constants";

const ratioToPercent = (ratio: number) => ratio * 100;

const formatPercent = (ratio: number): string => {
  const percent = ratioToPercent(ratio);

  return `${percent % 1 === 0 ? percent : percent.toFixed(1)}%`;
};

const formatYAxisLabel = (value: NumberLike) => `${Math.round(value.valueOf())}%`;

const formatTooltipValue = (value: number) => `${value.toFixed(1)}%`;

const DashboardWidgetsDoraChangeFailureRate = () => {
  const { rate, isLoading: isRateLoading } = useDoraChangeFailureRate();
  const { range, hasRuns } = useDoraChangeFailureRateRange();
  const { chartType } = useDoraChartType(CHART_TYPE_STORAGE_KEY);

  const hasData = hasRuns && !!range?.length;

  const grade = getDoraGrade(rate ?? 0, CHANGE_FAILURE_RATE_THRESHOLDS);

  // Convert ratio thresholds to percentage for bar coloring
  const gradeFairMax = useMemo(() => ratioToPercent(CHANGE_FAILURE_RATE_THRESHOLDS.fairMax), []);

  if (!hasData) {
    return (
      <DoraEmpty title="No failure data available" caption="Percentage of tracked runs that fail" />
    );
  }

  return (
    <Box direction="column" gap="x-large" fullWidth>
      <DoraAverage
        title="30d rate"
        formattedValue={formatPercent(rate ?? 0)}
        isLoading={isRateLoading}
        badge={grade && <Badge variant={grade.badgeVariant} type="weak" text={grade.label} />}
      />
      <DoraRange
        range={range}
        chartType={chartType}
        leftAxisLabel="Failure rate %"
        formatYAxisLabel={formatYAxisLabel}
        formatTooltipValue={formatTooltipValue}
        convertValue={ratioToPercent}
        gradeFairMax={gradeFairMax}
      />
    </Box>
  );
};

export default DashboardWidgetsDoraChangeFailureRate;
